import { useEffect, useState } from 'react';
import moment from 'moment';
import { User } from 'services/auth/auth.types';
import { UserProjectRequest } from 'services/person/request/request.types';
import { useGetUserProfileMutation } from 'services/user/user.endpoint';
import { useGetUserProjectRequestsFromUserMutation, useUpdateRequestCommentMutation } from 'services/person/request/request.endpoint';

const PAGE_SIZE = 3;

export default function useMyRequestsVM() {
    const [me, setMe] = useState<User | null>(null);
    const [requests, setRequests] = useState<UserProjectRequest[]>([]);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [selectedRequest, setSelectedRequest] = useState<UserProjectRequest | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [getMe] = useGetUserProfileMutation();
    const [getRequests] = useGetUserProjectRequestsFromUserMutation();
    const [updateRequestComment] = useUpdateRequestCommentMutation();

    useEffect(() => {
        (async () => {
            const meRes = await getMe(undefined).unwrap();
            const meData = meRes.data;
            setMe(meData);
            const projectId = meData.projects?.[0]?.id;
            if (projectId) {
                const reqsRes = await getRequests({ id: projectId } as any).unwrap();
                const sorted = (reqsRes.data ?? []).slice().sort((a: any, b: any) =>
                    moment(b.created_at).valueOf() - moment(a.created_at).valueOf()
                );
                setRequests(sorted);
                if (sorted.length > 0) setSelectedRequest(sorted[0]);
            }
            setIsLoading(false);
        })();
    }, [getMe, getRequests]);

    const formatDate = (date: string) => moment(date).format('D MMMM YYYY');

    const visibleRequests = requests.slice(0, visibleCount);
    const hasMore = requests.length > visibleCount;
    const loadMore = () => setVisibleCount((c) => c + PAGE_SIZE);

    const handleSaveComment = async (value: string) => {
        if (!selectedRequest || !me?.projects?.[0]?.id) return;
        const res = await updateRequestComment({
            projectId: me.projects[0].id,
            requestId: selectedRequest.id as any,
            user_comment: value
        }).unwrap();
        const updated = res.data;
        setRequests((prev) => prev.map((r: any) => r.id === updated.id ? { ...r, ...updated } : r));
        setSelectedRequest((prev) => prev ? { ...prev, ...updated } : prev);
    };

    return {
        me,
        requests: visibleRequests,
        totalCount: requests.length,
        hasMore,
        loadMore,
        selectedRequest,
        setSelectedRequest,
        formatDate,
        isLoading,
        handleSaveComment
    };
}
